import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { messages } from '@fruitways/shared';
import { Banner, PageHeader, TabBar, Toast, type TabItem } from '@/components';
import { useIsAdmin } from '@/hooks/useAdmin';
import { useOnline } from '@/hooks/useOnline';
import { useOutbox } from '@/hooks/useOutbox';
import { useToast } from '@/hooks/useToast';

type TabKey = 'home' | 'estimates' | 'queue' | 'data';

const TAB_PATH: Record<TabKey, string> = {
  home: '/',
  estimates: '/estimates',
  queue: '/waiting',
  data: '/data',
};

export interface AppFrameProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  action?: ReactNode;
  /** Hide the tab bar on flows with a pinned action pair (capture, review, detail). */
  tabs?: boolean;
  /** Banner copy with no signal, when the screen needs something other than the default. */
  offlineMessage?: string;
  children: ReactNode;
}

/** Page anatomy: header, optional offline banner, content, optional tab bar, toast. */
export function AppFrame({
  title,
  subtitle,
  onBack,
  action,
  tabs = true,
  offlineMessage,
  children,
}: AppFrameProps) {
  const isAdmin = useIsAdmin();
  const { online } = useOnline();
  const { count } = useOutbox();
  const toast = useToast();
  const nav = useNavigate();
  const loc = useLocation();

  const active: TabKey = loc.pathname.startsWith('/estimates')
    ? 'estimates'
    : loc.pathname.startsWith('/waiting')
      ? 'queue'
      : loc.pathname.startsWith('/data')
        ? 'data'
        : 'home';

  const items: TabItem<TabKey>[] = [
    { key: 'home', label: messages.tabs.home, icon: 'home', iconOutline: 'home-outline' },
    { key: 'estimates', label: messages.tabs.estimates, icon: 'list', iconOutline: 'list-outline' },
    {
      key: 'queue',
      label: messages.tabs.waiting,
      icon: 'cloud-upload',
      iconOutline: 'cloud-upload-outline',
      ...(count ? { badge: count } : {}),
    },
    // Master data maintenance: admins only.
    ...(isAdmin
      ? [
          {
            key: 'data' as const,
            label: messages.tabs.data,
            icon: 'layers' as const,
            iconOutline: 'layers-outline' as const,
          },
        ]
      : []),
  ];

  return (
    <div className="fw-frame">
      <PageHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        backLabel={messages.common.back}
        action={action}
      />
      {!online && <Banner tone="offline">{offlineMessage ?? messages.banner.offline}</Banner>}
      {children}
      {tabs && <TabBar items={items} value={active} onChange={(k) => nav(TAB_PATH[k])} />}
      <Toast open={Boolean(toast.message)} onDismiss={toast.dismiss} noTabs={!tabs}>
        {toast.message}
      </Toast>
    </div>
  );
}
